import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import logo from '../../assets/img/hitch-lockup.svg';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '../../ui/core';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFormState } from 'react-hook-form';
import * as yup from 'yup';

import { useAuth, useEnvironment, useThemeMode } from '../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { changeEnvironment } from '../../redux/Environment/actions';
import { AuthActions } from '../../redux/Auth/actions';

const DISPLAY_FONT =
  '"Haas-Grot-Disp-App", "Haas-Grot-Text-App", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

// Color tokens declared as CSS custom properties. The active set is chosen by
// the resolved theme mode (see useThemeMode) and spread onto the page root, so
// the in-page theme toggle flips every styled element below — and the choice
// matches whatever the rest of the admin renders after login.
const darkTokens = {
  '--page-bg': '#000000',
  '--card-bg': 'rgba(255, 255, 255, 0.07)',
  '--card-border': 'rgba(255, 255, 255, 0.12)',
  '--card-shadow':
    '0 20px 80px rgba(0, 0, 0, 0.55), 0 1px 0 rgba(255, 255, 255, 0.08) inset, 0 -1px 0 rgba(0, 0, 0, 0.4) inset',
  '--text-primary': '#FFFFFF',
  '--text-secondary': 'rgba(255, 255, 255, 0.6)',
  '--text-label': 'rgba(255, 255, 255, 0.55)',
  '--text-label-focus': 'rgba(255, 255, 255, 0.9)',
  '--input-bg': 'rgba(255, 255, 255, 0.06)',
  '--input-bg-hover': 'rgba(255, 255, 255, 0.08)',
  '--input-bg-focus': 'rgba(255, 255, 255, 0.1)',
  '--input-border': 'rgba(255, 255, 255, 0.14)',
  '--input-border-hover': 'rgba(255, 255, 255, 0.28)',
  '--input-border-focus': 'rgba(255, 255, 255, 0.55)',
  '--input-autofill-shadow': 'rgba(255, 255, 255, 0.06)',
  '--btn-bg': 'rgba(255, 255, 255, 0.95)',
  '--btn-bg-hover': '#FFFFFF',
  '--btn-text': '#0a0a10',
  '--btn-shadow':
    '0 1px 0 rgba(255, 255, 255, 0.4) inset, 0 8px 24px rgba(0, 0, 0, 0.35)',
  '--btn-shadow-hover':
    '0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 10px 28px rgba(0, 0, 0, 0.4)',
  '--btn-disabled-bg': 'rgba(255, 255, 255, 0.12)',
  '--btn-disabled-text': 'rgba(255, 255, 255, 0.35)',
  '--segment-track-bg': 'rgba(255, 255, 255, 0.08)',
  '--segment-track-border': 'rgba(255, 255, 255, 0.1)',
  '--segment-active-bg': 'rgba(255, 255, 255, 0.18)',
  '--segment-active-shadow':
    '0 1px 0 rgba(255, 255, 255, 0.15) inset, 0 4px 14px rgba(0, 0, 0, 0.3)',
  '--segment-text': 'rgba(255, 255, 255, 0.55)',
  '--segment-text-hover': 'rgba(255, 255, 255, 0.8)',
  '--segment-text-active': '#FFFFFF',
  '--error-text': '#FF9A9E',
  '--logo-filter': 'invert(1) brightness(1.1)',
  '--logo-opacity': 0.95,
};

const lightTokens = {
  '--page-bg': '#FFFFFF',
  '--card-bg': 'rgba(255, 255, 255, 0.7)',
  '--card-border': 'rgba(0, 0, 0, 0.08)',
  '--card-shadow':
    '0 30px 60px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.8) inset, 0 0 0 1px rgba(0, 0, 0, 0.02) inset',
  '--text-primary': '#1d1d1f',
  '--text-secondary': 'rgba(0, 0, 0, 0.55)',
  '--text-label': 'rgba(0, 0, 0, 0.55)',
  '--text-label-focus': '#1d1d1f',
  '--input-bg': '#FFFFFF',
  '--input-bg-hover': '#FFFFFF',
  '--input-bg-focus': '#FFFFFF',
  '--input-border': 'rgba(0, 0, 0, 0.12)',
  '--input-border-hover': 'rgba(0, 0, 0, 0.24)',
  '--input-border-focus': '#1d1d1f',
  '--input-autofill-shadow': '#FFFFFF',
  '--btn-bg': '#1d1d1f',
  '--btn-bg-hover': '#000000',
  '--btn-text': '#FFFFFF',
  '--btn-shadow': 'none',
  '--btn-shadow-hover': 'none',
  '--btn-disabled-bg': '#e8e8ed',
  '--btn-disabled-text': 'rgba(0, 0, 0, 0.35)',
  '--segment-track-bg': 'rgba(0, 0, 0, 0.045)',
  '--segment-track-border': 'transparent',
  '--segment-active-bg': '#FFFFFF',
  '--segment-active-shadow': '0 1px 3px rgba(0, 0, 0, 0.08)',
  '--segment-text': 'rgba(0, 0, 0, 0.55)',
  '--segment-text-hover': 'rgba(0, 0, 0, 0.75)',
  '--segment-text-active': '#1d1d1f',
  '--error-text': '#c8243d',
  '--logo-filter': 'none',
  '--logo-opacity': 1,
};

const glassInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'var(--input-bg)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    transition: 'border-color 0.18s ease, background-color 0.18s ease',
    '& fieldset': { borderColor: 'var(--input-border)' },
    '&:hover fieldset': { borderColor: 'var(--input-border-hover)' },
    '&:hover': { backgroundColor: 'var(--input-bg-hover)' },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--input-border-focus)',
      borderWidth: '1.5px',
    },
    '&.Mui-focused': { backgroundColor: 'var(--input-bg-focus)' },
  },
  '& .MuiOutlinedInput-input': {
    padding: '18px 16px',
    fontSize: '16px',
    color: 'var(--text-primary)',
    caretColor: 'var(--text-primary)',
    '&::placeholder': { color: 'var(--text-label)' },
    '&:-webkit-autofill': {
      WebkitTextFillColor: 'var(--text-primary)',
      WebkitBoxShadow: '0 0 0 100px var(--input-autofill-shadow) inset',
      caretColor: 'var(--text-primary)',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '16px',
    color: 'var(--text-label)',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--text-label-focus)' },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': { fontSize: '13px' },
};

function ReloadPage() {
  useEffect(() => {
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  return (
    <Box display="flex" alignItems="center" gap="8px" mt="16px">
      <CircularProgress size="0.875rem" sx={{ color: 'var(--text-secondary)' }} />
      <Typography fontSize="13px" sx={{ color: 'var(--text-secondary)' }}>
        Reloading…
      </Typography>
    </Box>
  );
}

const EnvSegment = ({ active, label, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    sx={{
      px: { xs: '22px', sm: '24px' },
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '999px',
      cursor: 'pointer',
      fontSize: { xs: '14px', sm: '13px' },
      fontWeight: 500,
      letterSpacing: '-0.005em',
      color: active ? 'var(--segment-text-active)' : 'var(--segment-text)',
      bgcolor: active ? 'var(--segment-active-bg)' : 'transparent',
      boxShadow: active ? 'var(--segment-active-shadow)' : 'none',
      transition: 'all 0.2s ease',
      userSelect: 'none',
      WebkitTapHighlightColor: 'transparent',
      '&:hover': {
        color: active ? 'var(--segment-text-active)' : 'var(--segment-text-hover)',
      },
    }}
  >
    {label}
  </Box>
);

const THEME_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

// Compact pill that reuses the same frosted-segment material as the
// environment switch. Sets the persisted, app-wide theme preference.
// Implements the ARIA radiogroup keyboard pattern: roving tabindex (only
// the checked option is in the tab order), arrow keys move + select, and
// Space/Enter selects the focused option.
const ThemeToggle = ({ mode, onChange }) => {
  const activeIndex = Math.max(
    0,
    THEME_OPTIONS.findIndex((opt) => opt.value === mode),
  );

  const handleKeyDown = (event) => {
    const last = THEME_OPTIONS.length - 1;
    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = activeIndex === last ? 0 : activeIndex + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = activeIndex === 0 ? last : activeIndex - 1;
        break;
      case ' ':
      case 'Enter':
        nextIndex = activeIndex;
        break;
      default:
        return;
    }
    event.preventDefault();
    const next = THEME_OPTIONS[nextIndex];
    onChange(next.value);
    // Move focus to the newly-selected option to keep arrow navigation going.
    event.currentTarget.parentNode
      ?.querySelector(`[data-value="${next.value}"]`)
      ?.focus();
  };

  return (
    <Box
      role="radiogroup"
      aria-label="Color theme"
      sx={{
        display: 'inline-flex',
        p: '3px',
        bgcolor: 'var(--segment-track-bg)',
        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',
        border: '1px solid var(--segment-track-border)',
        borderRadius: '999px',
      }}
    >
      {THEME_OPTIONS.map((opt) => {
        const active = mode === opt.value;
        return (
          <Box
            key={opt.value}
            data-value={opt.value}
            onClick={() => onChange(opt.value)}
            onKeyDown={handleKeyDown}
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            sx={{
              px: '14px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '999px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: active ? 'var(--segment-text-active)' : 'var(--segment-text)',
              bgcolor: active ? 'var(--segment-active-bg)' : 'transparent',
              boxShadow: active ? 'var(--segment-active-shadow)' : 'none',
              transition: 'all 0.2s ease',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                color: active ? 'var(--segment-text-active)' : 'var(--segment-text-hover)',
              },
              '&:focus-visible': {
                outline: '2px solid var(--input-border-focus)',
                outlineOffset: '2px',
              },
            }}
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
};

const Login = () => {
  const auth = useAuth();
  const history = useHistory();
  const { mode, resolvedMode, setMode } = useThemeMode();
  const { environment, allowChangingEnvironment } = useEnvironment();
  const dispatch = useDispatch();
  const loginError = useSelector((state) => state.auth.error);
  const [doReloadPage, setDoReloadPage] = useState(false);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        email: yup.string().required('Email is required').email('Email not valid'),
        password: yup.string().required('Password is required'),
      }),
    ),
  });
  const { isDirty, isValid, isSubmitting } = useFormState({ control: form.control });

  const onSubmit = (data) => {
    auth.login(data.email, data.password);
  };

  useEffect(() => {
    if (auth.token) {
      history.push('/home');
    }
  }, [auth.token, history]);

  useEffect(() => {
    const subscription = form.watch(() => {
      if (loginError) {
        dispatch({ type: AuthActions.RESET_ERROR });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, loginError, dispatch]);

  return (
    <Box
      sx={{
        ...(resolvedMode === 'light' ? lightTokens : darkTokens),
        position: 'relative',
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--page-bg)',
        px: { xs: 2.5, sm: 4 },
        pt: { xs: 6, sm: 10 },
        pb: { xs: 5, sm: 8 },
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 2,
        }}
      >
        <ThemeToggle mode={mode} onChange={setMode} />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box mb={{ xs: 5, sm: 8 }}>
          <Box
            component="img"
            alt="Hitch"
            src={logo}
            sx={{
              height: { xs: '26px', sm: '30px' },
              display: 'block',
              filter: 'var(--logo-filter)',
              opacity: 'var(--logo-opacity)',
            }}
          />
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: '460px',
            borderRadius: '28px',
            padding: { xs: '32px 24px 28px', sm: '44px 40px 36px' },
            backgroundColor: 'var(--card-bg)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid var(--card-border)',
            boxShadow: 'var(--card-shadow)',
          }}
        >
          <Box mb={{ xs: 3.5, sm: 4.5 }} textAlign="center">
            <Typography
              component="h1"
              sx={{
                fontFamily: DISPLAY_FONT,
                fontSize: 'clamp(30px, 7vw, 44px)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.025em',
                color: 'var(--text-primary)',
                mb: { xs: 0.75, sm: 1 },
                whiteSpace: 'nowrap',
              }}
            >
              Command Center
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.45,
                fontWeight: 400,
                color: 'var(--text-secondary)',
                letterSpacing: '-0.005em',
              }}
            >
              Sign in to continue
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={form.handleSubmit((data) => onSubmit(data))}
            width="100%"
            display="flex"
            flexDirection="column"
            gap="12px"
          >
            <TextField
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              control={form.control}
              fullWidth
              sx={glassInputSx}
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              control={form.control}
              fullWidth
              sx={glassInputSx}
            />

            {loginError ? (
              <Typography
                role="alert"
                aria-live="polite"
                sx={{
                  mt: '4px',
                  px: '4px',
                  fontSize: '13px',
                  lineHeight: 1.4,
                  color: 'var(--error-text)',
                  letterSpacing: '-0.005em',
                }}
              >
                Incorrect email or password. Please try again.
              </Typography>
            ) : null}

            <Button
              type="submit"
              disabled={!(isValid && isDirty)}
              loading={isSubmitting}
              variant="contained"
              disableElevation
              fullWidth
              sx={{
                mt: '14px',
                height: '52px',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                textTransform: 'none',
                color: 'var(--btn-text)',
                backgroundColor: 'var(--btn-bg)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                boxShadow: 'var(--btn-shadow)',
                transition: 'background-color 0.18s ease, transform 0.06s ease',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'var(--btn-bg-hover)',
                  boxShadow: 'var(--btn-shadow-hover)',
                },
                '&:active': { transform: 'scale(0.99)' },
                '&.Mui-disabled': {
                  backgroundColor: 'var(--btn-disabled-bg)',
                  color: 'var(--btn-disabled-text)',
                  boxShadow: 'none',
                },
              }}
            >
              Log In
            </Button>
          </Box>
        </Box>

        {allowChangingEnvironment ? (
          <Box
            mt={{ xs: 3.5, sm: 5 }}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Box
              sx={{
                display: 'inline-flex',
                p: '4px',
                bgcolor: 'var(--segment-track-bg)',
                backdropFilter: 'blur(30px)',
                WebkitBackdropFilter: 'blur(30px)',
                border: '1px solid var(--segment-track-border)',
                borderRadius: '999px',
              }}
            >
              <EnvSegment
                active={environment === 'production'}
                label="Production"
                onClick={() => {
                  changeEnvironment('production')(dispatch);
                  setDoReloadPage(true);
                }}
              />
              <EnvSegment
                active={environment === 'staging'}
                label="Staging"
                onClick={() => {
                  changeEnvironment('staging')(dispatch);
                  setDoReloadPage(true);
                }}
              />
            </Box>
            {doReloadPage ? <ReloadPage /> : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default Login;
